import { create } from 'zustand';
import { IActivity, InputStroke } from '@eduwrite/shared';

export interface CompletedTask {
    activity: IActivity;
    strokes: InputStroke[];
    structuredData?: unknown;
    score?: number;
}

interface ActivityState {
    activities: IActivity[];
    pendingActivities: IActivity[];
    currentActivityIndex: number;
    isLoading: boolean;
    error: string | null;
    sessionHistory: CompletedTask[];
    studentId: string | null; // DB UUID of the active student — used for task completion

    fetchActivities: (classId: string) => Promise<void>;
    nextActivity: () => void;
    resetActivities: () => void;
    pushActivity: (activity: IActivity) => void;
    pushActivities: (activities: IActivity[]) => void;
    pushActivityFromConfig: (config: Record<string, unknown>) => void;
    startPendingActivity: (activityId?: string) => void;
    jumpToActivity: (targetText: string) => void;
    addCompletedTask: (task: CompletedTask) => void;
    clearSessionHistory: () => void;
    loadPreFetchedTasks: (tasks: IActivity[]) => void;
    setStudentId: (id: string | null) => void;
}

export const useActivityStore = create<ActivityState>((set, get) => ({
    activities: [],
    pendingActivities: [],
    currentActivityIndex: 0,
    isLoading: false,
    error: null,
    sessionHistory: [],
    studentId: null,

    jumpToActivity: (targetText: string) => {
        const { activities } = get();
        const index = activities.findIndex(a => (a.config as Record<string, unknown>)?.targetText === targetText);
        if (index >= 0) {
            set({ currentActivityIndex: index });
        }
    },

    fetchActivities: async (_classId: string) => {
        set({ isLoading: true, error: null });
        try {
            // Student app now waits for teacher to push the first activity
            set({ activities: [], isLoading: false });
        } catch (err: unknown) {
            set({ error: (err as Error).message, isLoading: false });
        }
    },

    nextActivity: () => {
        const { activities, currentActivityIndex } = get();
        if (currentActivityIndex < activities.length - 1) {
            set({ currentActivityIndex: currentActivityIndex + 1 });
        }
    },

    resetActivities: () => {
        set({ currentActivityIndex: 0, activities: [], pendingActivities: [], sessionHistory: [], studentId: null });
    },

    pushActivity: (activity: IActivity) => {
        const { activities, pendingActivities, currentActivityIndex } = get();
        const activityId = activity.assignmentId || activity.id;
        const exists = activities.some(a => (a.assignmentId || a.id) === activityId) ||
                       pendingActivities.some(a => (a.assignmentId || a.id) === activityId);
        if (exists) return;

        // If there's no active session or the active session is completed, start immediately
        const isActuallyFinished = activities.length > 0 && currentActivityIndex >= activities.length - 1 && get().sessionHistory.length >= activities.length;

        if (activities.length === 0 || isActuallyFinished) {
            set({ activities: [activity], currentActivityIndex: 0 });
        } else {
            // Otherwise, add to pending queue
            set((state) => ({ pendingActivities: [...state.pendingActivities, activity] }));
        }
    },

    pushActivities: (newActivities: IActivity[]) => {
        if (!newActivities.length) return;
        const { activities, pendingActivities } = get();
        const existingIds = new Set([
            ...activities.map(a => a.assignmentId || a.id),
            ...pendingActivities.map(a => a.assignmentId || a.id)
        ]);
        const filtered = newActivities.filter(a => !existingIds.has(a.assignmentId || a.id));
        if (!filtered.length) return;

        if (activities.length === 0) {
            set({ activities: filtered, currentActivityIndex: 0 });
        } else {
            set((state) => ({ pendingActivities: [...state.pendingActivities, ...filtered] }));
        }
    },

    pushActivityFromConfig: (config: Record<string, unknown>) => {
        // Legacy: wrap a raw TracingActivityConfig in an IActivity shell
        const newActivity: IActivity = {
            id: `pushed-${Date.now()}`,
            type: 'letter_tracing',
            title: config.targetText ? `Trace: ${config.targetText}` : 'Teacher Task',
            instructions: config.targetText ? `Please draw: ${config.targetText}` : 'New activity',
            config,
        };
        get().pushActivity(newActivity);
    },

    startPendingActivity: (activityId?: string) => {
        const { pendingActivities } = get();
        if (pendingActivities.length === 0) return;

        let activitiesToStart: IActivity[];

        if (activityId) {
            const specific = pendingActivities.find(a => a.id === activityId);
            if (specific) {
                // Start with the specific one, followed by the rest of the current pending batch
                activitiesToStart = [specific, ...pendingActivities.filter(a => a.id !== activityId)];
            } else {
                activitiesToStart = [...pendingActivities];
            }
        } else {
            // Move everything from pending to active
            activitiesToStart = [...pendingActivities];
        }

        set({
            activities: activitiesToStart,
            currentActivityIndex: 0,
            pendingActivities: []
        });
    },

    addCompletedTask: (task: CompletedTask) => {
        set((state) => ({ sessionHistory: [...state.sessionHistory, task] }));
    },

    clearSessionHistory: () => {
        set({ sessionHistory: [] });
    },

    /**
     * Pre-loads tasks fetched from the DB at login time.
     * If tasks are found, they become the initial activity list.
     * If empty, the student is in waiting mode (teacher will push live).
     */
    loadPreFetchedTasks: (tasks: IActivity[]) => {
        if (tasks.length > 0) {
            set({ activities: tasks, currentActivityIndex: 0 });
        } else {
            set({ activities: [], currentActivityIndex: 0 });
        }
    },

    setStudentId: (id: string | null) => {
        set({ studentId: id });
    },
}));

